import { useCallback } from 'react'
import log from 'loglevel'
import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { useAccountReceptors } from 'src/components/async/model/receptor/useReceptors'
import { useDocumentPickerIfAvailable } from 'src/components/DocumentPicker'
import { pickFolderScanReceptor } from 'src/pages/Receptors/folderScanReceptor.helpers'
import { useHasNewDocumentPicker } from 'src/context/Gatekeeper'
import { useGoogleDriveContext } from '../../context/GoogleDriveContext'
import { LINKER_MODEL_IDS } from '../../Linkers.constants'
import { useGooglePickerLegacy } from './GoogleDriveWizardLegacy.hooks'
import {
  PickerConfiguration,
  PickerResponse,
} from './GoogleDriveWizard.constants'

/**
 * Google Drive picking.
 *
 * Browsing runs in the document picker, through ntrced, under the receptor's
 * own service-account identity: the user only ever sees what Trustero can
 * read, and no Google sign-in happens in the browser. The picked nodes come
 * back as `filesSelected` on GoogleDriveContext, so the Add Evidence / Add
 * Document flows that read it keep working unchanged.
 */

const NOT_SHOWN: PickerResponse = {
  isShown: false,
  invalidCredentials: false,
  exceptions: '',
}

/** Kept for callers that gated on the Google scripts; nothing loads any more. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useLoadGoogleApi = (_shouldLoad = true): boolean => true

type ReceptorLike =
  | ReceptorRecord
  | (Partial<ReceptorRecord.AsObject> & { id?: string })
  | undefined

export const useGooglePicker = (): ((
  config: PickerConfiguration,
  receptor?: ReceptorLike,
) => Promise<PickerResponse>) => {
  // Accounts not on the document picker keep the vendor Picker, which runs
  // under the signed-in user's Google token and checks each pick against
  // the receptor.
  const newPicker = useHasNewDocumentPicker()
  const openLegacyPicker = useGooglePickerLegacy(!newPicker)
  const {
    receptor: contextReceptor,
    source,
    subjectIds,
  } = useGoogleDriveContext()
  const { data: accountReceptors } = useAccountReceptors()
  const { openModal } = useModalState()
  const documentPicker = useDocumentPickerIfAvailable()

  // Callers hand over a ReceptorRecord, its toObject() form, or nothing (the
  // flows that open the picker right after connecting the linker). The
  // document picker wants the record. With nothing to go on, the usable
  // connection wins (enabled, valid credentials), never the first row.
  const resolveReceptor = useCallback(
    (receptor: ReceptorLike): ReceptorRecord | undefined => {
      if (receptor instanceof ReceptorRecord) return receptor
      const id = receptor?.id
      if (id) {
        const found = accountReceptors
          ?.getReceptorsList()
          .find((r) => r.getId() === id)
        if (found) return found
      }
      return (
        pickFolderScanReceptor(
          accountReceptors?.getReceptorsList(),
          LINKER_MODEL_IDS.GOOGLE_DRIVE,
        ) || contextReceptor
      )
    },
    [accountReceptors, contextReceptor],
  )

  return useCallback(
    async (
      config: PickerConfiguration,
      receptorLike?: ReceptorLike,
    ): Promise<PickerResponse> => {
      if (!newPicker) return openLegacyPicker(config, receptorLike)
      const receptor = resolveReceptor(receptorLike)
      if (!receptor?.getCredentials()?.length) {
        openModal(ModalFormId.CONNECT_LINKER)
        return NOT_SHOWN
      }
      const exceptions = receptor.getExceptions()
      if (exceptions?.length > 0) {
        return { isShown: false, invalidCredentials: false, exceptions }
      }
      if (!receptor.getIscredvalid()) {
        return { isShown: false, invalidCredentials: true, exceptions: '' }
      }

      if (!documentPicker) {
        log.error('Google Drive picker opened outside DocumentPickerProvider')
        return NOT_SHOWN
      }
      documentPicker.openPicker({
        receptor,
        mode: config.selectFolderEnabled ? 'folders' : 'files',
        source: source === 'Policy' ? 'Policy' : 'Evidence',
        subjectId: subjectIds[0],
        parentModalId: config.modalId,
        lockConnection: config.lockConnection,
      })
      return { isShown: true, invalidCredentials: false, exceptions: '' }
    },
    [
      newPicker,
      openLegacyPicker,
      resolveReceptor,
      openModal,
      documentPicker,
      source,
      subjectIds,
    ],
  )
}
